import apiClient from './client';

// ─── Übersichten, Systemzustand und Einstellungen ───────────────────────────

export interface BaumZeile {
  id: string;
  name: string;
  personen: number;
  beziehungen: number;
  medien: number;
  medienBytes: number;
  mitglieder: number;
  besitzer: string | null;
  angelegtAm: string;
}

export async function fetchBaumUebersicht(): Promise<BaumZeile[]> {
  const { data } = await apiClient.get<{ data: BaumZeile[] }>('/admin/trees-overview');
  return data.data;
}

export interface KontenUebersicht {
  gesamt: number;
  aktiv: number;
  betreiber: number;
  wartendAufFreischaltung: number;
  mitZweiFaktor: number;
  neueste: { email: string; angelegtAm: string; aktiv: boolean }[];
}

export async function fetchKontenUebersicht(): Promise<KontenUebersicht> {
  const { data } = await apiClient.get<KontenUebersicht>('/admin/accounts-overview');
  return data;
}

export interface Systemzustand {
  node: string;
  umgebung: string;
  laufzeitSekunden: number;
  hostname: string;
  speicher: { rssBytes: number; systemFreiBytes: number; systemGesamtBytes: number };
  datenbank: {
    erreichbar: boolean;
    version: string | null;
    groesseBytes: number | null;
    verbindungen: number | null;
  };
  ablage: { pfad: string; beschreibbar: boolean; dateien: number; bytes: number };
  sicherung: {
    hetznerKonfiguriert: boolean;
    letzterLauf: string | null;
    letzterStatus: 'success' | 'error' | null;
    naechsterLauf: string | null;
  };
  letzteFehler: { action: string; userEmail: string | null; createdAt: string }[];
}

export async function fetchSystemzustand(): Promise<Systemzustand> {
  const { data } = await apiClient.get<Systemzustand>('/admin/system');
  return data;
}

export interface Einstellungen {
  /** Rückfallebene, falls kein Tarif greift. */
  baumKontingent: number;
  /** Anmeldeversuche je Zeitfenster. */
  loginMaxVersuche: number;
  /** Länge des Zeitfensters in Minuten. Wirkt erst nach einem Neustart. */
  loginFensterMinuten: number;
  /** Aufbewahrung der Protokolleinträge in Tagen. 0 = für immer. */
  protokollAufbewahrungTage: number;
  umgebung: {
    appUrl: string | null;
    adminDomain: string | null;
    uploadDir: string;
    hetznerKonfiguriert: boolean;
    trustProxy: string;
  };
}

export async function fetchEinstellungen(): Promise<Einstellungen> {
  const { data } = await apiClient.get<Einstellungen>('/admin/settings');
  return data;
}

export interface EinstellungsAenderung {
  baumKontingent?: number;
  loginMaxVersuche?: number;
  loginFensterMinuten?: number;
  protokollAufbewahrungTage?: number;
}

export async function saveEinstellungen(a: EinstellungsAenderung): Promise<Einstellungen> {
  const { data } = await apiClient.put<Einstellungen>('/admin/settings', a);
  return data;
}

// ─── Texte der Installation ─────────────────────────────────────────────────
//
// Die Definitionen kommen vom Server mit, statt sie hier zweitzuführen: sonst
// zeigte die Konsole nach einem Update Felder, die es nicht mehr gibt, oder
// ließe neue aus.

export interface TextDefinition {
  key: string;
  label: string;
  ort: string;
  vorgabe: string;
  lang?: boolean;
  gruppe: 'Anwendung' | 'Anmeldung' | 'Rechtliches' | 'Benachrichtigungen';
  /** Platzhalter, die dieser Text kennt – ohne die geschweiften Klammern. */
  platzhalter?: string[];
}

export interface TexteAntwort {
  definitionen: TextDefinition[];
  /** Nur die tatsächlich abweichenden Werte. Leer = Vorgabe. */
  werte: Record<string, string>;
}

export async function fetchTexte(): Promise<TexteAntwort> {
  const { data } = await apiClient.get<TexteAntwort>('/admin/texts');
  return data;
}

export async function saveTexte(werte: Record<string, string>): Promise<TexteAntwort['werte']> {
  const { data } = await apiClient.put<{ werte: Record<string, string> }>('/admin/texts', werte);
  return data.werte;
}
